import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { classNames } from 'primereact/utils'

import { useToastContext } from '../../../../../../contexts/toast'
import {
  OrderResponse,
  OrderResponseItem,
  PatchRequest,
  WebEdiDto,
} from '../../../../../../data/models'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import { isOrderModified } from '../../../../../../utils/helper/checkConfirmationCodes'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../utils/helper/validateFormikFields'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config/context'
import { useOrderItems } from '../../../../data'
import {
  requestPatchOrderById,
  requestPutOrderItemById,
} from '../../../../data/api'
import { getConfirmationCode } from '../../../../utils'

interface Props {
  visible: boolean
  setVisible: (params: boolean) => void
  items: OrderResponseItem[] | null | undefined
  handleItems: (params: any) => void
  id: string
}

interface FormValues {
  confirmationDeliveryDate: Date | undefined
}

type FormFieldName = 'confirmationDeliveryDate'

export const EditDate: React.FC<Props> = ({
  visible,
  setVisible,
  items,
  handleItems,
  id,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()
  // const { mutate: globalMutate } = useSWRConfig()

  const { data } = useOrderItems(id)

  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  // ************************************************************
  // Formik
  // ************************************************************
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      confirmationDeliveryDate: undefined,
    },
    validate: formData => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'confirmationDeliveryDate',
          config: config.orderResponse.editOrderItem.confirmationDeliveryDate,
        },
      ]

      validateFormikFields(fieldsToValidate, formData, errors)

      const minDate =
        items && items[0] && items[0].MinConfirmationDeliveryDate
          ? new Date(items[0].MinConfirmationDeliveryDate)
          : undefined
      const maxDate =
        items && items[0] && items[0].MaxConfirmationDeliveryDate
          ? new Date(items[0].MaxConfirmationDeliveryDate)
          : undefined

      if (formData.confirmationDeliveryDate && minDate) {
        const minDeliveryDate = new Date(minDate)
        const confirmationDeliveryDate = new Date(
          formData.confirmationDeliveryDate,
        )

        if (confirmationDeliveryDate < minDeliveryDate) {
          errors.confirmationDeliveryDate = t(
            'orderResponse.positions.table.edit.dialog.form.confirmationDeliveryDate.validation.minDeliveryDate',
            { date: minDeliveryDate.toLocaleDateString() },
          )
        }
      }

      if (formData.confirmationDeliveryDate && maxDate) {
        const maxDeliveryDate = new Date(maxDate)
        const confirmationDeliveryDate = new Date(
          formData.confirmationDeliveryDate,
        )

        if (confirmationDeliveryDate > maxDeliveryDate) {
          errors.confirmationDeliveryDate = t(
            'orderResponse.positions.table.edit.dialog.form.confirmationDeliveryDate.validation.maxDeliveryDate',
            { date: maxDeliveryDate.toLocaleDateString() },
          )
        }
      }

      return errors
    },
    onSubmit: async formData => {
      // TODO: onsubmit überall gleich aufbauen
      // deactivate the buttons
      setIsRequestRunning(true)

      try {
        // close the dialog and reset
        handleClose()

        // submit form
        await submitForm(formData)

        // mutate()
        // globalMutate(`/OrderResponses/${id}/Items`)

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'orderResponse.positions.table.editDeliveryDate.dialog.save.messages.success.summary',
          ),
          detail: t(
            'orderResponse.positions.table.editDeliveryDate.dialog.save.messages.success.detail',
            {
              count: items?.length,
            },
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t(
            'orderResponse.positions.table.editDeliveryDate.dialog.save.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  const handleClose = () => {
    setVisible(false) // hide the dialog
    formik.resetForm()
    handleItems([]) // deselect the items in the table
  }

  const submitForm = async (formData: FormValues) => {
    const updatedItems = [...(data as OrderResponseItem[])]

    const currentDate = formData.confirmationDeliveryDate?.toISOString()

    items?.forEach(async item => {
      // make a copy of the item
      const newOrderResponseItem = produce(item, draft => {
        // testing for code rejected is not necessary because it is not possible to select this items
        if (item.ConfirmationCode === 'REJECTED') {
          return // if the product is rejected don't change the delivery date
        }

        draft.ConfirmationDeliveryDate = currentDate

        draft.ConfirmationCode = getConfirmationCode(draft, item)

        // we have changed the value of ConfirmationAllowanceChargeSum to boolean
        // to filter it so we have to replace it with the original value again
        data!.forEach(d => {
          if (draft.Id === d.Id) {
            draft.ConfirmationAllowanceChargeSum =
              d.ConfirmationAllowanceChargeSum
          }
        })
      })

      const itemIndex = updatedItems.findIndex(
        i => i.Id === newOrderResponseItem.Id,
      )

      if (itemIndex >= 0) {
        updatedItems[itemIndex] = newOrderResponseItem
      } else {
        updatedItems.push(newOrderResponseItem)
      }

      // patch for ConfirmationCode
      const newDataArray: OrderResponseItem[] =
        data?.map(item => {
          // Check if the current item is present in updatedItems
          const updatedItem = updatedItems.find(
            updated => updated.Id === item.Id,
          )
          // If yes, use the updated item; otherwise, keep the original item
          return updatedItem ? updatedItem : item
        }) || []

      const notSetCode: OrderResponse['ConfirmationCode'] = 'NOT_SET'
      const modifiedCode: OrderResponse['ConfirmationCode'] = 'MODIFIED'
      const patchData: PatchRequest<WebEdiDto<OrderResponse>> = [
        {
          op: 'replace',
          path: '/Payload/ConfirmationCode',
          value: isOrderModified(newDataArray) ? modifiedCode : notSetCode,
        },
      ]

      await requestPutOrderItemById(id, item.Id, newOrderResponseItem)
      await requestPatchOrderById(id, patchData)
    })
  }

  // ************************************************************
  // Components
  // ************************************************************
  // confirmationDeliveryDate
  const confirmationDeliveryDateLabel = (
    <label
      htmlFor="confirmationDeliveryDate"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationDeliveryDate'),
      })}
    ></label>
  )

  const minDate =
    items && items[0] && items[0].MinConfirmationDeliveryDate
      ? new Date(items[0].MinConfirmationDeliveryDate)
      : undefined
  const maxDate =
    items && items[0] && items[0].MaxConfirmationDeliveryDate
      ? new Date(items[0].MaxConfirmationDeliveryDate)
      : undefined

  const confirmationDeliveryDateComponent = (
    <Calendar
      id="confirmationDeliveryDate"
      value={formik.values.confirmationDeliveryDate}
      onChange={e => {
        const newDate = e
        const confirmationDeliveryDate = new Date(
          items![0].ConfirmationDeliveryDate!,
        )
        if (newDate && confirmationDeliveryDate) {
          newDate.value?.setHours(
            confirmationDeliveryDate.getHours(),
            confirmationDeliveryDate.getMinutes(),
            confirmationDeliveryDate.getSeconds(),
          )
        }

        formik.handleChange(newDate)
      }}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationDeliveryDate'),
      })}
      showButtonBar
      showIcon
      minDate={minDate}
      maxDate={maxDate}
      locale={language}
    />
  )

  const confirmationDeliveryDateErrorMsg = (
    <div>{getFormErrorMessage('confirmationDeliveryDate')}</div>
  )

  const confirmationDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.editOrderItem}
      component={confirmationDeliveryDateComponent}
      label={confirmationDeliveryDateLabel}
      errorMsg={confirmationDeliveryDateErrorMsg}
    />
  )

  // header
  const header = (
    <>
      {t('orderResponse.positions.table.editDeliveryDate.dialog.title', {
        count: items?.length,
      })}
    </>
  )

  // footer
  const footer = (
    <>
      <Button
        label={t(
          'orderResponse.positions.table.editDeliveryDate.dialog.cancel.label',
        )}
        icon="pi pi-times"
        className="p-button-text w-full md:w-auto mb-1 md:mb-0"
        onClick={handleClose}
      />
      <Button
        label={t(
          'orderResponse.positions.table.editDeliveryDate.dialog.save.label',
        )}
        icon={'pi pi-check'}
        className="w-full md:w-auto"
        onClick={formik.submitForm}
        type="submit"
        disabled={!formik.dirty}
        loading={isRequestRunning}
      />
    </>
  )

  return (
    <Dialog
      visible={visible}
      style={{ width: '30rem' }}
      breakpoints={{ [calculateBreakpointWidth(30)]: '90vw' }}
      header={header}
      footer={footer}
      onHide={handleClose}
      blockScroll
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="formgrid grid">
          <div className="col">{confirmationDeliveryDateWrapper}</div>
        </div>
      </form>
    </Dialog>
  )
}
